import React, { useState, useEffect, useRef } from 'react';
import { getApiUrl, API_ENDPOINTS } from '../config/apiConfig';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion, useScroll, useTransform } from 'framer-motion';
import { FaStar, FaCalendarAlt, FaPlay, FaCheck, FaHeart, FaLayerGroup, FaList, FaClock } from 'react-icons/fa';

import Header from '../components/Header';
import EpisodeSlider from '../components/EpisodeSlider';
import SEO from '../components/SEO';
import { getImageUrl } from '../utils/imageHelper';

const MOBILE_BREAKPOINT = 768;

const DetailPageContainer = styled.div`
  width: 100%;
  position: relative;
  overflow-x: hidden;
  background-color: var(--background-start);
  min-height: 100vh;
  padding-bottom: ${props => props.$isMobile ? '80px' : '0'};
`;

const BackdropWrapper = styled.div`
  position: relative;
  width: 100%;
  height: ${props => props.$isMobile ? '50vh' : '80vh'};
  z-index: 1;
`;

const BackdropImage = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 110%;
  background-image: url(${props => props.backdrop});
  background-size: cover;
  background-position: center 30%;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to top, var(--background-start) 30%, transparent 70%);
  }
`;

const MainContent = styled.div`
  position: relative;
  z-index: 10;
  margin-top: ${props => props.$isMobile ? '-20vh' : '-35vh'};
`;

const InfoSection = styled.div`
  padding: ${props => props.$isMobile ? '0 5% 30px 5%' : '0 5% 40px 5%'};
  display: flex;
  flex-direction: ${props => props.$isMobile ? 'column' : 'row'};
  gap: ${props => props.$isMobile ? '30px' : '50px'};
  align-items: ${props => props.$isMobile ? 'center' : 'flex-end'};
`;

const Poster = styled(motion.img)`
  width: ${props => props.$isMobile ? '200px' : '300px'};
  height: ${props => props.$isMobile ? '300px' : '450px'};
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.7);
  flex-shrink: 0;
`;

const Info = styled(motion.div)`
  flex-grow: 1;
  width: ${props => props.$isMobile ? '100%' : 'auto'};
  text-align: ${props => props.$isMobile ? 'center' : 'left'};
`;

const Title = styled.h1`
  font-size: ${props => props.$isMobile ? '2rem' : '3rem'};
  font-weight: 800;
  margin-bottom: 5px;
  line-height: 1.1;
  
  @media (max-width: 480px) {
    font-size: 1.8rem;
  }
`;

const OriginalTitle = styled.h2`
  font-size: ${props => props.$isMobile ? '0.9rem' : '1.1rem'};
  font-weight: 500;
  color: var(--secondary-color);
  margin-bottom: 20px;
  font-style: italic;
`;

const MediaTypeBadge = styled.span`
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  margin-left: 10px;
  vertical-align: middle;
  
  ${props => {
    switch (props.type) {
      case 'anime':
        return `
          background: linear-gradient(135deg, #ff6b6b, #ee5a24);
          color: white;
        `;
      case 'tv':
        return `
          background: linear-gradient(135deg, #4ecdc4, #00b894);
          color: #111;
        `;
      case 'movie':
        return `
          background: linear-gradient(135deg, #45b7d1, #0984e3);
          color: white;
        `;
      default:
        return `
          background: #636e72;
          color: white;
        `;
    }
  }}
`;

const MetaInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${props => props.$isMobile ? 'center' : 'flex-start'};
  flex-wrap: wrap;
  gap: ${props => props.$isMobile ? '15px' : '20px'};
  margin-bottom: 20px;
  color: var(--secondary-color);
  font-size: ${props => props.$isMobile ? '0.9rem' : '1rem'};
  
  span { 
    display: flex; 
    align-items: center; 
    gap: 8px; 
  }
`;

const Rating = styled.span`
  color: #ffc107;
  font-weight: 700;
`;

const GenresContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 25px;
  justify-content: ${props => props.$isMobile ? 'center' : 'flex-start'};
`;

const GenreTag = styled.div`
  padding: 5px 12px;
  background: var(--glass-bg);
  border: 1px solid var(--border-color);
  border-radius: 20px;
  font-size: ${props => props.$isMobile ? '0.7rem' : '0.8rem'};
  font-weight: 500;
`;

const Overview = styled.p`
  font-size: ${props => props.$isMobile ? '0.9rem' : '0.95rem'};
  line-height: 1.6;
  max-width: ${props => props.$isMobile ? '100%' : '80%'};
  color: var(--secondary-color);
  margin: ${props => props.$isMobile ? '0 auto' : '0'};
  text-align: ${props => props.$isMobile ? 'center' : 'left'};
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 30px;
  justify-content: ${props => props.$isMobile ? 'center' : 'flex-start'};
  flex-wrap: ${props => props.$isMobile ? 'wrap' : 'nowrap'};
`;

const StyledButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: ${props => props.$isMobile ? '12px 20px' : '12px 25px'};
  border-radius: 10px;
  font-size: ${props => props.$isMobile ? '0.9rem' : '1rem'};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  flex: ${props => props.$isMobile ? '1' : 'none'};
  min-width: ${props => props.$isMobile ? '140px' : 'auto'};
  
  background: rgba(20, 10, 30, 0.6);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--primary-color);

  &:hover {
    border-color: rgba(255, 0, 127, 0.7);
    background: rgba(20, 10, 30, 0.8);
  }
`;

const FavoriteButton = styled(StyledButton)`
  background: ${props => props.$isFavorite ? 'rgba(255, 0, 127, 0.3)' : 'rgba(20, 10, 30, 0.6)'};
  border-color: ${props => props.$isFavorite ? 'rgba(255, 0, 127, 0.7)' : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.$isFavorite ? '#ff007f' : 'var(--primary-color)'};
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100vh;
  font-size: 2rem;
`;

const MobileSpacer = styled.div`
  height: ${props => props.$isMobile ? '20px' : '0'};
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: 100px 20px;
  color: var(--secondary-color);
  
  h2 {
    color: #ff6b6b;
    margin-bottom: 20px;
  }
`;

const DetailPage = () => {
  const { mediaType, id } = useParams();
  const navigate = useNavigate();
  const [contentDetails, setContentDetails] = useState(null);
  const [categories, setCategories] = useState([]);
  const [alternateTitles, setAlternateTitles] = useState([]);
  const [isFavorite, setIsFavorite] = useState(false);
  const [seasonCount, setSeasonCount] = useState(0);
  const [episodeCount, setEpisodeCount] = useState(0);
  const [avgDuration, setAvgDuration] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT);
  const [user, setUser] = useState(null);
  const [error, setError] = useState('');

  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef, offset: ["start start", "end start"] });
  const backdropY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Kullanıcı kontrolü
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(getApiUrl(API_ENDPOINTS.CHECK_AUTH), {
          credentials: 'include'
        });
        const data = await response.json();
        if (data.success) {
          setUser(data.user);
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error('Auth kontrol hatası:', error);
        setUser(null);
      }
    };

    checkAuth();
  }, []);

  const getImageUrl = (path, size = 'w500') => {
    // Path kontrolü - boş, null, undefined veya sadece whitespace ise data URI döndür
    if (!path || path === 'null' || path === 'undefined' || (typeof path === 'string' && path.trim() === '')) {
      // Path yoksa direkt data URI döndür (placeholder spam'ı önlemek için)
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iIzExMSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTIiIGZpbGw9IiM2NjYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5SZXNpbSBZb2s8L3RleHQ+PC9zdmc+';
    }

    // String'e çevir ve trim yap
    path = String(path).trim();

    // Eğer path zaten proxy URL ise direkt döndür
    if (path.includes('/proxy-image.php')) return path;
    
    // Zaten data URI ise direkt döndür
    if (path.startsWith('data:')) return path;

    // Eğer path tam bir TMDB URL'si ise, sadece path kısmını çıkar
    if (path.includes('image.tmdb.org')) {
      const match = path.match(/\/t\/p\/[^/]+\/(.+)$/);
      if (match) {
        path = '/' + match[1];
      }
    }

    // Path'i normalize et - başında / olmalı (http/https ile başlamıyorsa)
    let normalizedPath = path;
    if (!normalizedPath.startsWith('/') && !normalizedPath.startsWith('http')) {
      normalizedPath = '/' + normalizedPath;
    }

    // Proxy URL oluştur
    return `${getApiUrl(API_ENDPOINTS.PROXY_IMAGE)}?path=${encodeURIComponent(normalizedPath)}&size=${size}`;
  };

  // Veritabanından içerik detaylarını çekme
  useEffect(() => {
    const loadDetails = async () => {
      setIsLoading(true);
      setError('');
      window.scrollTo(0, 0);
      
      try {
        // MySQL'den içerik detaylarını çek - tmdb_id ile
        const response = await fetch(`${getApiUrl(API_ENDPOINTS.GET_CONTENT)}?tmdb_id=${id}`);
        
        if (!response.ok) {
          throw new Error('İçerik bulunamadı');
        }

        const contentData = await response.json();
        
        // Eğer API error döndürüyorsa
        if (contentData.error) {
          throw new Error(contentData.error);
        }

        // İçeriğin sitede olup olmadığını kontrol et
        if (!contentData || !contentData.id) {
          throw new Error('Bu içerik sitede bulunmuyor');
        }

        setContentDetails(contentData);

        // Sezon ve bölüm sayısını çek (sadece tv/anime için)
        if (contentData.media_type === 'tv' || contentData.media_type === 'anime') {
          try {
            const seasonsResponse = await fetch(`${getApiUrl(API_ENDPOINTS.GET_SEASONS)}?content_id=${contentData.id}`);
            if (seasonsResponse.ok) {
              const seasonsData = await seasonsResponse.json();
              setSeasonCount(seasonsData.length || 0);
              
              // Tüm bölümleri çek ve say
              let totalEpisodes = 0;
              let totalDuration = 0;
              let episodesWithDuration = 0;
              
              for (const season of seasonsData) {
                const episodesResponse = await fetch(`${getApiUrl(API_ENDPOINTS.GET_EPISODES)}?season_id=${season.id}`);
                if (episodesResponse.ok) {
                  const episodesData = await episodesResponse.json();
                  totalEpisodes += episodesData.length || 0;
                  
                  // Duration'ları topla
                  episodesData.forEach(ep => {
                    if (ep.duration && ep.duration > 0) {
                      totalDuration += parseInt(ep.duration);
                      episodesWithDuration++;
                    }
                  });
                }
              }
              
              setEpisodeCount(totalEpisodes);
              
              // Ortalama süreyi hesapla
              if (episodesWithDuration > 0) {
                setAvgDuration(Math.round(totalDuration / episodesWithDuration));
              }
            }
          } catch (seasonsError) {
            console.error('Sezon/bölüm bilgileri çekilirken hata:', seasonsError);
          }
        }

        // Kategorileri çek
        try {
          const categoriesResponse = await fetch(`${getApiUrl(API_ENDPOINTS.GET_CATEGORIES)}?content_id=${contentData.id}`);
          if (categoriesResponse.ok) {
            const categoriesData = await categoriesResponse.json();
            if (categoriesData.success) {
              setCategories(categoriesData.categories);
            }
          }
        } catch (categoriesError) {
          console.error('Kategoriler çekilirken hata:', categoriesError);
        }

        // Alternatif başlıkları çek
        try {
          const titlesResponse = await fetch(`${getApiUrl(API_ENDPOINTS.GET_ALTERNATE_TITLES)}?content_id=${contentData.id}`);
          if (titlesResponse.ok) {
            const titlesData = await titlesResponse.json();
            // API'den gelen veriyi normalize et - string array'e çevir
            let titles = [];
            if (Array.isArray(titlesData)) {
              titles = titlesData.map(t => {
                // Eğer object ise title property'sini al, değilse direkt kullan
                if (typeof t === 'string') return t;
                if (t && typeof t === 'object') {
                  return t.title || t.name || t.alternate_title || String(t);
                }
                return String(t);
              }).filter(t => t && typeof t === 'string' && t.trim() !== '');
            }
            setAlternateTitles(titles);
          }
        } catch (titlesError) {
          console.error('Alternatif başlıklar çekilirken hata:', titlesError);
        }

        // Favori kontrolü (sadece giriş yapmış kullanıcılar için)
        if (user) {
          try {
            const favResponse = await fetch(`${getApiUrl(API_ENDPOINTS.CHECK_FAVORITE)}?content_id=${contentData.id}`, {
              credentials: 'include'
            });
            
            if (favResponse.ok) {
              const favData = await favResponse.json();
              setIsFavorite(favData.isFavorite);
            }
          } catch (favError) {
            console.error('Favori kontrol hatası:', favError);
          }
        }

      } catch (error) {
        console.error("Detaylar çekilirken hata oluştu:", error);
        setError(error.message);
        setContentDetails(null);
      } finally {
        setIsLoading(false);
      }
    };
    
    if (id) {
      loadDetails();
    }
  }, [id, user]);

  const toggleFavorite = async () => {
    if (!user) {
      alert('Favorilere eklemek için giriş yapın!');
      navigate('/login');
      return;
    }

    if (!contentDetails) return;

    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.TOGGLE_FAVORITE), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content_id: parseInt(contentDetails.id),
          media_type: contentDetails.media_type
        }),
        credentials: 'include'
      });

      const data = await response.json();

      if (data.success) {
        setIsFavorite(data.isFavorite);
      } else {
        alert(data.message);
      }
    } catch (error) {
      console.error("Favori işlemi sırasında hata:", error);
      alert('Favori işlemi sırasında hata oluştu');
    }
  };

  const handleWatch = () => {
    if (!contentDetails) return;

    const watchMediaType = contentDetails.media_type === 'anime' ? 'tv' : contentDetails.media_type;
    
    if (watchMediaType === 'movie') {
      navigate(`/watch/movie/${contentDetails.tmdb_id}`);
    } else {
      // İlk sezon ve ilk bölüme yönlendir
      navigate(`/watch/tv/${contentDetails.tmdb_id}/season/1/episode/1`);
    }
  };

  const containerVariants = { 
    hidden: { opacity: 0 }, 
    visible: { 
      opacity: 1, 
      transition: { 
        staggerChildren: 0.1 
      } 
    } 
  };
  
  const itemVariants = { 
    hidden: { 
      opacity: 0, 
      y: 20 
    }, 
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { 
        duration: 0.5 
      } 
    } 
  };

  // vote_average'ı güvenli şekilde formatla
  const getVoteAverage = () => {
    if (!contentDetails || !contentDetails.vote_average) return 'N/A';
    
    const vote = parseFloat(contentDetails.vote_average);
    return isNaN(vote) ? 'N/A' : vote.toFixed(1);
  };

  if (isLoading) {
    return (
      <DetailPageContainer ref={containerRef} $isMobile={isMobile}>
        <Header />
        <LoadingContainer>
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            style={{ marginRight: '15px' }}
          >
            ⭐
          </motion.div>
          Yükleniyor...
        </LoadingContainer>
      </DetailPageContainer>
    );
  }

  if (error || !contentDetails) {
    return (
      <DetailPageContainer $isMobile={isMobile}>
        <Header />
        <ErrorContainer>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {error || 'İçerik bulunamadı'}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            Bu içerik sitede mevcut değil veya erişilemiyor.
          </motion.p>
          <motion.button
            onClick={() => navigate('/')}
            style={{
              marginTop: '20px',
              padding: '12px 24px',
              background: 'var(--gradient-pink)',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              cursor: 'pointer',
              fontSize: '1rem'
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Ana Sayfaya Dön
          </motion.button>
        </ErrorContainer>
      </DetailPageContainer>
    );
  }

  // Media type etiketi
  const getMediaTypeLabel = () => {
    switch (contentDetails.media_type) {
      case 'anime': return 'Anime';
      case 'tv': return 'Dizi';
      case 'movie': return 'Film';
      default: return contentDetails.media_type;
    }
  };

  // Yıl bilgisini al
  const getYear = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).getFullYear();
    } catch {
      return 'N/A';
    }
  };

  // SEO için içerik bilgilerini hazırla
  const seoTitle = contentDetails 
    ? `${contentDetails.title} Türkçe İzle - CineGlass`
    : 'CineGlass - Türkçe Dublaj ve Altyazılı Film ve Dizi İzle';
  
  const seoDescription = contentDetails
    ? `${contentDetails.title} ${contentDetails.media_type === 'movie' ? 'film' : 'dizi'} Türkçe dublaj ve altyazılı olarak CineGlass'ta izleyin. ${contentDetails.overview ? contentDetails.overview.substring(0, 150) : ''} Ücretsiz ve HD kalitede.`
    : 'CineGlass ile en yeni filmler ve dizileri Türkçe dublaj ve altyazılı olarak izleyin.';
  
  // SEO Keywords - alternatif başlıklarla birlikte
  const seoKeywords = (() => {
    if (!contentDetails) return 'film izle, dizi izle, türkçe dublaj, türkçe altyazı';
    
    // String kontrolü ekle - sadece string olanları al
    const allTitles = [
      contentDetails.title,
      ...alternateTitles
    ].filter(t => {
      // String kontrolü ve boş olmayan kontrolü
      return typeof t === 'string' && t.trim() !== '';
    });
    
    const keywordsParts = [];
    
    allTitles.forEach(title => {
      keywordsParts.push(`${title} türkçe izle`);
      keywordsParts.push(`${title} türkçe dublaj`);
      keywordsParts.push(`${title} türkçe altyazı`);
    });
    
    keywordsParts.push(contentDetails.media_type === 'movie' ? 'film izle' : 'dizi izle');
    keywordsParts.push('türkçe film izle');
    keywordsParts.push('türkçe dizi izle');
    
    return keywordsParts.join(', ');
  })();
  
  const seoImage = contentDetails?.backdrop_path 
    ? getImageUrl(contentDetails.backdrop_path, 'original')
    : contentDetails?.poster_path
    ? getImageUrl(contentDetails.poster_path, 'original')
    : '';

  return (
    <DetailPageContainer ref={containerRef} $isMobile={isMobile}>
      <SEO 
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        image={seoImage}
        url={window.location.href}
        type={contentDetails?.media_type === 'movie' ? 'video.movie' : 'video.tv_show'}
      />
      <Header />
      
      <BackdropWrapper $isMobile={isMobile}>
            <BackdropImage 
              backdrop={contentDetails.backdrop_path ? getImageUrl(contentDetails.backdrop_path, 'original') : ''} 
              style={{ y: backdropY }}
            />
      </BackdropWrapper>
  
      <MainContent $isMobile={isMobile}>
        <InfoSection $isMobile={isMobile}>
              <Poster 
                src={getImageUrl(contentDetails.poster_path, 'w500')} 
                alt={contentDetails.title}
                onError={(e) => {
                  // Eğer zaten data URI ise, tekrar deneme (sonsuz döngüyü önle)
                  if (e.target.src.startsWith('data:')) {
                    e.target.onerror = null;
                    return;
                  }
                  // Data URI'ye geç
                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iIzExMSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTIiIGZpbGw9IiM2NjYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5SZXNpbSBZb2s8L3RleHQ+PC9zdmc+';
                  e.target.onerror = null;
                }}
              />
            <Info as={motion.div} variants={containerVariants} initial="hidden" animate="visible" $isMobile={isMobile}>
                <motion.div variants={itemVariants}>
                    <Title $isMobile={isMobile}>
                      {contentDetails.title}
                      <MediaTypeBadge type={contentDetails.media_type}>
                        {getMediaTypeLabel()}
                      </MediaTypeBadge>
                    </Title>
                </motion.div>
                
                <motion.div variants={itemVariants}>
                  {contentDetails.original_title && contentDetails.original_title !== contentDetails.title && (
                    <OriginalTitle $isMobile={isMobile}>{contentDetails.original_title}</OriginalTitle>
                  )}
                </motion.div>
                
                <motion.div variants={itemVariants}>
                    <MetaInfo $isMobile={isMobile}>
                        <Rating>
                          <FaStar /> 
                          {getVoteAverage()}
                        </Rating>
                        <span>
                          <FaCalendarAlt /> 
                          {getYear(contentDetails.release_date)}
                        </span>
                        {(contentDetails.media_type === 'tv' || contentDetails.media_type === 'anime') && seasonCount > 0 && (
                          <>
                            <span>
                              <FaLayerGroup /> {seasonCount}
                            </span>
                            {episodeCount > 0 && (
                              <span>
                                <FaList /> {episodeCount}
                              </span>
                            )}
                            {avgDuration && (
                              <span>
                                <FaClock /> {avgDuration} dk
                              </span>
                            )}
                          </>
                        )}
                    </MetaInfo>
                </motion.div>

                {/* KATEGORİLER */}
                {Array.isArray(categories) && categories.length > 0 && (
                  <motion.div variants={itemVariants}>
                    <GenresContainer $isMobile={isMobile}>
                      {categories.map((category, index) => (
                        <GenreTag key={index} $isMobile={isMobile}>
                          {category}
                        </GenreTag>
                      ))}
                    </GenresContainer>
                  </motion.div>
                )}
                
                <motion.div variants={itemVariants}>
                    <Overview $isMobile={isMobile}>
                      {contentDetails.overview || 'Açıklama bulunmuyor.'}
                    </Overview>
                </motion.div>
                
                <motion.div variants={itemVariants}>
                    <ButtonContainer $isMobile={isMobile}>
                        <StyledButton onClick={handleWatch} $isMobile={isMobile}>
                            <FaPlay /> İzle
                        </StyledButton>
                        <FavoriteButton 
                            onClick={toggleFavorite}
                            $isFavorite={isFavorite}
                            $isMobile={isMobile}
                        >
                            {isFavorite ? <FaCheck /> : <FaHeart />} 
                            {isFavorite ? 'Favorilerde' : 'Favorilere Ekle'}
                        </FavoriteButton>
                    </ButtonContainer>
                </motion.div>
            </Info>
        </InfoSection>

        <MobileSpacer $isMobile={isMobile} />

        {/* TV ve Anime için bölüm slider'ı */}
        {(contentDetails.media_type === 'tv' || contentDetails.media_type === 'anime') && (
            <motion.div variants={itemVariants}>
                <EpisodeSlider 
                  contentId={contentDetails.id}
                  tmdbId={contentDetails.tmdb_id}
                  isMobile={isMobile} 
                  isAnime={contentDetails.media_type === 'anime'}
                  backdropPath={contentDetails.backdrop_path}
                  posterPath={contentDetails.poster_path}
                />
            </motion.div>
        )}
      </MainContent>
    </DetailPageContainer>
  );
};

export default DetailPage;
